import { DateTime } from '@ksyos/date-time';
import { Alert, Button, FormButtonRow, FormGroup, FormSection, Radio, RadioGroup, Text } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import { ProcessEntity } from '@common/enums/processEntity';
import { getSelectedLanguage } from '@frontend/translate';
import type { IPatient } from '../../../../../../common/interfaces/patient';
import type { IServiceGetOne } from '../../../../interfaces/serviceGetOne';
import { CancelButton } from '../../../../elements/carePathButtons/CancelButton';

type RequestCriteriaProps = {
    onCancel: () => Promise<void>;
    onContinue: () => Promise<void>;
    patient: IPatient;
    service: IServiceGetOne;
};

type FormGroup = {
    automation: string;
    label: string;
    name: FormGroupName;
    tooltip: string;
};

type FormGroupName =
    | 'acuteVisionLoss'
    | 'childUnder8Years'
    | 'infectionInflammation'
    | 'somethingInEye'
    | 'spotsBlurFlashes';

type RequestCriteriaState = Record<FormGroupName, boolean | null>;

const formGroups: FormGroup[] = [
    {
        automation: 'acuteVisusDaling',
        label: 'Acute visus daling',
        name: 'acuteVisionLoss',
        tooltip: 'Acute visusdaling/gezichtsvelduitval (binnen enkele dagen ontstaan)',
    },
    {
        automation: 'ietsInHetOog',
        label: 'Iets in het oog',
        name: 'somethingInEye',
        tooltip: 'Chemische stof of corpus alienum (bijv. metaaldeeltjes, glassplinters of verf op het hoornvlies)',
    },
    {
        automation: 'vlekkenTroebelingenFlitsen',
        label: 'Plots vlekken, troebelingen, flitsen',
        name: 'spotsBlurFlashes',
        tooltip:
            'Plots ontstaan van vlekken, troebelingen en/of flitsen gepaard gaande met gezichtsvelduitval of geobjectiveerde visusdaling',
    },
    {
        automation: 'infectieOntsteking',
        label: 'Infectie / ontsteking',
        name: 'infectionInflammation',
        tooltip: 'Rood oog met infectie/ontstekingsbeeld',
    },
    {
        automation: 'kindOnderDe8Jaar',
        label: 'Kind onder de 8 jaar',
        name: 'childUnder8Years',
        tooltip: 'Kinderen onder de 8 jaar komen niet in aanmerking voor dit type onderzoek',
    },
];
const acuteConditions: FormGroupName[] = [
    'acuteVisionLoss',
    'infectionInflammation',
    'somethingInEye',
    'spotsBlurFlashes',
];

export const RequestCriteria = ({ patient, onCancel, onContinue, service }: RequestCriteriaProps) => {
    const translate = getSelectedLanguage();

    const [isUnder8yearsOld, setIsUnder8yearsOld] = useState<boolean | null>(null);

    const [formErrors, setFormErrors] = useState({
        acuteCondition: false,
        childUnder8Years: false,
        someNull: false,
    });

    const [isSubmitted, setIsSubmitted] = useState(false);

    const [state, setState] = useState<RequestCriteriaState>({
        acuteVisionLoss: null,
        childUnder8Years: isUnder8yearsOld,
        infectionInflammation: null,
        somethingInEye: null,
        spotsBlurFlashes: null,
    });

    useEffect(() => {
        if (patient.birthDate) {
            const isUnder8YearsOld = DateTime.now().diff(patient.birthDate, 'years') < 8;
            setIsUnder8yearsOld(isUnder8YearsOld);
        }
    }, [patient.birthDate]);

    return (
        <FormSection>
            {formGroups.map(({ name, label, tooltip }) => (
                <FormGroup
                    hasErrors={isSubmitted && state[name] === null}
                    isRequired
                    key={name}
                    label={label}
                    layout="horizontal"
                    tooltipText={tooltip}
                >
                    <RadioGroup
                        error={isSubmitted && state[name] === null ? translate.validation.any_custom : undefined}
                        layout="row"
                        name={name}
                        onChange={(e) => handleItemChange(name, e.target.value === 'yes')}
                        value={getRadioValue(name)}
                    >
                        <Radio
                            label={translate.common.yes}
                            value="yes"
                        ></Radio>
                        <Radio
                            label={translate.common.no}
                            value="no"
                        ></Radio>
                    </RadioGroup>
                </FormGroup>
            ))}

            {showErrorBanner(formErrors) && (
                <Alert appearance="warning">
                    {getErrorMessages(formErrors).map(
                        ({ condition, message }) =>
                            condition && (
                                <Text
                                    color="warning"
                                    fontWeight="bold"
                                    key={message}
                                >
                                    {message}
                                </Text>
                            )
                    )}
                </Alert>
            )}

            <FormButtonRow>
                <CancelButton
                    cancel={onCancel}
                    entity={ProcessEntity.request}
                    key="cancel"
                    serviceCode={service.code}
                    serviceType={service.serviceType}
                />
                <Button
                    data-testid="continue button request criteria"
                    key="continue"
                    onClick={handleContinueClick}
                >
                    {translate.common.proceed}
                </Button>
            </FormButtonRow>
        </FormSection>
    );

    function getRadioValue(name: FormGroupName): string {
        if (state[name] === null) {
            return '';
        } else {
            return state[name] ? 'yes' : 'no';
        }
    }

    function handleItemChange(name: FormGroupName, value: boolean) {
        setState((prevState) => {
            const newState = { ...prevState, [name]: value };
            const newErrors = validateState(newState);
            newErrors.someNull = false; // don't show this error message when the user is still filling in the form
            setFormErrors(newErrors);

            return newState;
        });
    }

    async function handleContinueClick() {
        setIsSubmitted(true);
        const newErrors = validateState(state);
        setFormErrors(newErrors);

        if (hasTrue(newErrors)) {
            return;
        } else {
            await onContinue();
        }
    }

    function hasTrue(obj: Record<string, boolean>) {
        return Object.values(obj).some((value) => value);
    }

    function showErrorBanner(obj: Record<string, boolean>) {
        const errorsToCheck = ['acuteCondition', 'childUnder8Years'];

        return errorsToCheck.some((error) => obj[error]);
    }

    function getErrorMessages(errors: Record<string, boolean>) {
        const errorMessages = [
            {
                condition: errors.acuteCondition,
                message:
                    'Deze patiënt komt niet in aanmerking voor een optometrisch onderzoek. Vanwege de acute klacht verwijzen naar de oogarts.',
            },
            {
                condition: errors.childUnder8Years,
                message:
                    'Kinderen onder de 8 jaar komen niet in aanmerking voor een optometrisch onderzoek. Verwijs de patiënt naar een orthoptist.',
            },
        ];

        // Filter out the 'someNull' error
        return errorMessages.filter(({ condition }) => condition);
    }

    function validateState(stateToValidate: RequestCriteriaState) {
        const newErrors = {
            acuteCondition: acuteConditions.some((name) => stateToValidate[name]),
            childUnder8Years: Boolean(stateToValidate.childUnder8Years),
            someNull: Object.values(stateToValidate).some((val) => val === null),
        };

        // Check if all items are filled or all are false
        const allValues = Object.values(stateToValidate);
        const allFilled = allValues.every((val) => val !== null);
        const allFalse = allValues.every((val) => val === false);

        if (allFilled) {
            newErrors.someNull = false;
        }

        if (allFalse) {
            newErrors.acuteCondition = false;
            newErrors.childUnder8Years = false;
        }

        return newErrors;
    }
};
